import { getResponseOptions } from './getResponseOptions';
import { tracking } from '../strings.locstring.json';
import { isConnectedAccount } from 'owa-accounts-store';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { getCommandBarAddins } from 'owa-calendar-addins';
import { getCalendarEntryByFolderId } from 'owa-calendar-cache';
import { getCalendarItemCategoryMenuFilterableItem } from 'owa-calendar-categories-menu';
import type { FullComposeViewState } from 'owa-calendar-compose-types';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { DisplayOption, FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import { chosenShowAsOptionText, getShowAsMenuItems } from 'owa-freebusy-menu';
import loc, { format } from 'owa-localize';
import { saveButtonLabel } from 'owa-locstrings/lib/strings/savebuttonlabel.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { ReplyAllMenuItem } from 'owa-locstrings/lib/strings/replyallmenuitem.locstring.json';
import { isConsumer } from 'owa-session-store';
import {
    discardButtonLabel,
    openSchedulingAssistantTitle,
    showAsCommandBarAriaLabel,
} from './getComposeCommandBarItems.locstring.json';
import {
    canEditCategories,
    canReply,
    hasAttendees,
    isEventAMeeting,
    isEventInstanceOfSeries,
} from 'owa-calendar-event-capabilities';
import {
    onCategoriesChanged,
    onSensitivityToggled,
    presentDiscardClosureConfirmation,
    updateFreeBusy,
    updateSidePaneType,
    verifyEventThenSave,
    deleteEvent,
    closeComposeForm,
    openComposeSchedulingAssistant,
} from 'owa-calendar-compose-form-actions';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { customizeActionsText } from 'owa-locstrings/lib/strings/customizeActionsText.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { replyToMeeting } from 'owa-reply-to-meeting';
import {
    getIconFromProviderName,
    getJoinMeetingLabelFromProviderName,
} from 'owa-online-meeting/lib/utils/onlineMeetingHelper';
import {
    isValidOnlineMeeting,
    onJoinTeamsClicked,
    OnlineMeetingProviderType,
} from 'owa-online-meeting';
import { getDeleteButton } from 'owa-calendar-forms-common/lib/utils/command-bar/getDeleteButton';
import { getCancelButton } from 'owa-calendar-forms-common/lib/utils/command-bar/getCancelButton';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { duplicateEventButtonLabel } from 'owa-locstrings/lib/strings/duplicateeventbuttonlabel.locstring.json';
import { lazyDuplicateEvent } from 'owa-calendar-duplicate-event';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';

export default function getComposeCommandBarItems(
    item: CalendarEvent,
    originalItem: CalendarEvent,
    fullComposeViewState: FullComposeViewState,
    onDismissContextMenu?: () => void,
    calendarColorScheme?: ColorScheme,
    disabled?: boolean
): FilterableMenuItem[] {
    const canSendBehavior = () =>
        isEventAMeeting(item) &&
        isHostAppFeatureEnabled('saveInFullCompose') &&
        !isFeatureEnabled('mon-cal-saveButtonInlineInEdit');
    const canSaveBehavior = () =>
        !isEventAMeeting(item) &&
        isHostAppFeatureEnabled('saveInFullCompose') &&
        !isFeatureEnabled('mon-cal-saveButtonInlineInEdit');
    const canDiscardBehavior = () =>
        isHostAppFeatureEnabled('discardInFullCompose') &&
        !isFeatureEnabled('mon-cal-removeDiscardAndAddDeleteOrCancel');
    const shouldShowTrackingBehavior = () =>
        item.MeetingRequestWasSent && hasAttendees(originalItem) && hasAttendees(item);
    const onSaveOrSend = () => {
        updateContentToViewState(fullComposeViewState?.bodyViewState);
        verifyEventThenSave();
    };

    const canReplyAllBehavior = () => isFeatureEnabled('mon-cal-replyAllInEdit') && canReply(item);
    const onReplyAll = () => replyToMeeting(item, true /* isReplyAll */, 'EditForm');
    const isEventAnInstanceOfSeries: boolean = isEventInstanceOfSeries(item);

    const smtpOfCalendarOwner =
        item != null && item.ParentFolderId != null
            ? item.ParentFolderId.mailboxInfo.userIdentity
            : originalItem != null && originalItem.ParentFolderId != null
            ? originalItem.ParentFolderId.mailboxInfo.userIdentity
            : undefined;

    const isAConnectedAccount: boolean = isConnectedAccount(smtpOfCalendarOwner);

    // We need to have an additional check for connected account because the WebSessionType is not set to the correct value
    // for connected accounts
    const shouldShowSchedulingAssistant: boolean =
        !isConsumer(smtpOfCalendarOwner) && isHostAppFeatureEnabled('schedulingAssistant');
    const canShowCustomizedActionButton = () =>
        isFeatureEnabled('addin-exp-customizedActionsButtonNavBar');
    const shouldShowDuplicateButton = () =>
        isFeatureEnabled('mon-cal-DuplicateInEditForm') && !isNewEvent();
    const presentClosureConfirmation = () => presentDiscardClosureConfirmation();
    let items = [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(sendButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Send,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSendBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(saveButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Save,
            [FilterableMenuItemArgs.onClick]: onSaveOrSend,
            [FilterableMenuItemArgs.shouldShow]: canSaveBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(discardButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Delete,
            [FilterableMenuItemArgs.onClick]: presentClosureConfirmation,
            [FilterableMenuItemArgs.shouldShow]: canDiscardBehavior,
            [FilterableMenuItemArgs.key]: 'Discard',
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        ...(isFeatureEnabled('mon-cal-removeDiscardAndAddDeleteOrCancel')
            ? [
                  getDeleteButton(
                      item,
                      'EditForm',
                      true /*isFullForm*/,
                      isInPopOutWindow(),
                      deleteEvent
                  ),
              ]
            : []),
        ...(isFeatureEnabled('mon-cal-removeDiscardAndAddDeleteOrCancel')
            ? [
                  getCancelButton(item, 'EditForm', () => {
                      closeComposeForm(true /* shouldResetStore */, item.IsMeeting);
                  }),
              ]
            : []),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(ReplyAllMenuItem),
            [FilterableMenuItemArgs.icon]: ControlIcons.ReplyAll,
            [FilterableMenuItemArgs.onClick]: onReplyAll,
            [FilterableMenuItemArgs.shouldShow]: canReplyAllBehavior,
            [FilterableMenuItemArgs.key]: 'ReplyAll',
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        getJoinOnlineFilterableMenuItem(item, disabled),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(openSchedulingAssistantTitle),
            [FilterableMenuItemArgs.icon]: ControlIcons.ScheduleEventAction,
            [FilterableMenuItemArgs.onClick]: () => {
                updateSidePaneType(null /* formId */, SidePaneType.None); // Close all sidepanes when we open scheduling assistant
                openComposeSchedulingAssistant(null /* formId */);
            },
            [FilterableMenuItemArgs.shouldShow]: () => shouldShowSchedulingAssistant,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.key]: 'SchedulingAssistant',
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(tracking),
            [FilterableMenuItemArgs.icon]: ControlIcons.PeopleAlert,
            [FilterableMenuItemArgs.onClick]: () =>
                fullComposeViewState.currentSidePaneType == SidePaneType.Tracking
                    ? updateSidePaneType(null /* formId */, SidePaneType.None)
                    : updateSidePaneType(null /* formId */, SidePaneType.Tracking),
            [FilterableMenuItemArgs.shouldShow]: shouldShowTrackingBehavior,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: chosenShowAsOptionText(item),
            [FilterableMenuItemArgs.icon]: ControlIcons.ShowTimeAs,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
            [FilterableMenuItemArgs.disabled]: disabled,
            [FilterableMenuItemArgs.menuContent]: {
                items: getShowAsMenuItems(
                    item,
                    updateFreeBusy /* onClick */,
                    calendarColorScheme,
                    onSensitivityToggled,
                    !isEventAnInstanceOfSeries /* showPrivateOption */
                ),
                onDismiss: onDismissContextMenu,
            },
            [FilterableMenuItemArgs.key]: 'ShowAs',
            [FilterableMenuItemArgs.ariaLabel]: format(
                loc(showAsCommandBarAriaLabel),
                chosenShowAsOptionText(item)
            ),
        }),
        getCalendarItemCategoryMenuFilterableItem(
            getCalendarEntryByFolderId(item.ParentFolderId.Id),
            item,
            'CommandBar',
            categoriesChangedHandler,
            categoriesChangedHandler,
            categoriesChangedHandler,
            !isEventAnInstanceOfSeries && canEditCategories(item),
            !isAConnectedAccount && isHostAppFeatureEnabled('settings'), // show manage categories
            true, // show search
            onDismissContextMenu,
            ControlIcons.Tag,
            disabled
        ),
        getResponseOptions(item, disabled, onDismissContextMenu),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(duplicateEventButtonLabel),
            [FilterableMenuItemArgs.icon]: ControlIcons.Copy,
            [FilterableMenuItemArgs.onClick]: () => {
                const isPopOut = isInPopOutWindow();
                presentDiscardClosureConfirmation(() => {
                    lazyDuplicateEvent.importAndExecute(
                        item,
                        'ComposeCommandBarDuplicate' /* entrySource */,
                        isPopOut ? 'PopOut' : 'Modal',
                        true /* use default scope */
                    );
                });
            },
            [FilterableMenuItemArgs.shouldShow]: shouldShowDuplicateButton,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
        }),
        ...(item.ParentFolderId && isHostAppFeatureEnabled('addIns')
            ? getCommandBarAddins(
                  ExtensibilityModeEnum.AppointmentOrganizer,
                  item,
                  item.ParentFolderId.Id,
                  disabled
              )
            : []),
        //Customize action button being added here
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(customizeActionsText),
            [FilterableMenuItemArgs.icon]: ControlIcons.Settings,
            [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
            [FilterableMenuItemArgs.onClick]: () =>
                lazyMountAndShowFullOptions.importAndExecute(
                    'calendar',
                    'calendarSurfaceActions',
                    'calendarSurfaceAddins'
                ),
            [FilterableMenuItemArgs.shouldShow]: canShowCustomizedActionButton,
            [FilterableMenuItemArgs.disabled]: disabled,
        }),
    ];

    return items;
}

function getJoinOnlineFilterableMenuItem(
    item: CalendarEvent,
    disabled?: boolean
): FilterableMenuItem {
    const canJoinOnlineMeetingBehavior = () =>
        isFeatureEnabled('mon-cal-joinOnlineMeetingInEdit') && isValidOnlineMeeting(item, true);

    const onJoinOnlineMeeting = () => {
        onJoinTeamsClicked(item, 'EditForm' /* ViewType */, () =>
            window.open(item.OnlineMeetingJoinUrl, '_blank')
        );
    };

    const onlineMeetingProviderName = item.OnlineMeetingProvider as OnlineMeetingProviderType;
    const joinOnlineMeetingLabel = getJoinMeetingLabelFromProviderName(onlineMeetingProviderName);
    const joinOnlineMeetingIcon = getIconFromProviderName(onlineMeetingProviderName);

    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: joinOnlineMeetingLabel,
        [FilterableMenuItemArgs.icon]: joinOnlineMeetingIcon,
        [FilterableMenuItemArgs.onClick]: onJoinOnlineMeeting,
        [FilterableMenuItemArgs.shouldShow]: canJoinOnlineMeetingBehavior,
        [FilterableMenuItemArgs.key]: 'JoinOnlineMeeting',
        [FilterableMenuItemArgs.disabled]: disabled,
    });
}

// this is to avoid rerender when parent component changes - https://flexport.engineering/optimizing-react-rendering-part-1-9634469dca02 - Section ES6 arrow function/bind
const categoriesChangedHandler = newCategoriesList => onCategoriesChanged(newCategoriesList);
